import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ShieldAlert } from 'lucide-react'
import { CredentialBlock } from '@/components/custom-modules/credential-block'
import { PageHeader } from '@/components/layout/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { clearCredentials, peekCredentials } from '@/lib/external-credentials-cache'

export const Route = createFileRoute('/settings/custom-modules/$key/credentials')({
  component: CredentialsPage,
})

function CredentialsPage() {
  const { key } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()

  if (!key.startsWith('external:')) {
    return (
      <>
        <PageHeader
          title={t('builderRuntime.credentials.title')}
          subtitle={t('builderRuntime.credentials.subtitle')}
        />
        <Alert variant="destructive">
          <AlertDescription>{t('customModules.credentials.wrongType')}</AlertDescription>
        </Alert>
      </>
    )
  }

  const connectionId = key.slice('external:'.length)
  const creds = peekCredentials(connectionId)

  if (!creds) {
    return (
      <>
        <PageHeader
          title={t('builderRuntime.credentials.title')}
          subtitle={t('builderRuntime.credentials.subtitle')}
        />
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>{t('customModules.credentials.alreadyShown')}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Link to="/settings/custom-modules/$key" params={{ key }}>
            <Button>{t('customModules.credentials.backToModule')}</Button>
          </Link>
        </div>
      </>
    )
  }

  const onConfirm = () => {
    clearCredentials(connectionId)
    void navigate({ to: '/settings/custom-modules/$key', params: { key } })
  }

  return (
    <>
      <PageHeader
        title={t('builderRuntime.credentials.title')}
        subtitle={t('builderRuntime.credentials.subtitle')}
      />
      <Card>
        <CardContent className="pt-6 space-y-6">
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>{t('customModules.credentials.warning')}</AlertDescription>
          </Alert>

          <CredentialBlock
            label={t('customModules.credentials.organizationId')}
            value={creds.organizationId}
            alwaysVisible
            helpText={t('customModules.credentials.organizationIdHelp')}
          />

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('customModules.credentials.inboundGroup')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('customModules.credentials.inboundDescription')}
            </p>
            <CredentialBlock
              label={t('customModules.credentials.hmacSecret')}
              value={creds.sharedSecret}
              helpText={t('customModules.credentials.hmacSecretHelp')}
            />
            <CredentialBlock
              label={t('customModules.credentials.oidcClientId')}
              value={creds.oidcClientId}
              alwaysVisible
              helpText={t('customModules.credentials.oidcClientIdHelp')}
            />
            <CredentialBlock
              label={t('customModules.credentials.oidcClientSecret')}
              value={creds.oidcClientSecret}
              helpText={t('customModules.credentials.oidcClientSecretHelp')}
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">
              {t('customModules.credentials.outboundGroup')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('customModules.credentials.outboundDescription')}
            </p>
            <CredentialBlock
              label={t('customModules.credentials.serviceToken')}
              value={creds.serviceToken}
              helpText={t('customModules.credentials.serviceTokenHelp')}
            />
          </section>

          <div className="flex justify-end pt-2">
            <Button onClick={onConfirm}>{t('customModules.credentials.confirm')}</Button>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
